import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, addDoc, getDocs, query, where, Timestamp, setLogLevel } from 'firebase/firestore';
import { Calendar, CheckCircle, XCircle, BarChart2, MapPin, List, PlusCircle, Server, FileText, ChevronLeft, ChevronRight, AlertTriangle, Clock, HardHat } from 'lucide-react';

// --- Firebase Configuration ---
const firebaseConfig = {
    apiKey: "AIzaSyCNvQS7us6zi-z6BpTAPZG4W2vKL5gT7EI",
    authDomain: "dolly-sweep-tracker-df946.firebaseapp.com",
    projectId: "dolly-sweep-tracker-df946",
    storageBucket: "dolly-sweep-tracker-df946.firebasestorage.app",
    messagingSenderId: "961259124866",
    appId: "1:961259124866:web:8967beb33209ec0a741c9b"
};


// --- Environment Variable Handling ---
// This safely checks for special variables from the online environment.
// eslint-disable-next-line no-undef
const appId = typeof __app_id !== 'undefined' ? __app_id : 'dolly-sweep-app';


// --- Main App Component (Entry Point) ---
// This component handles the initial config check to avoid React Hook errors.
export default function App() {
    // Check for placeholder config before doing anything else.
    if (firebaseConfig.apiKey === "YOUR_API_KEY" || !firebaseConfig.projectId) {
        return (
             <div className="bg-slate-100 min-h-screen font-sans text-slate-800">
                <div className="container mx-auto p-4 max-w-7xl">
                    <Header />
                    <FirebaseConfigError />
                    <Footer />
                </div>
            </div>
        );
    }

    // If config is valid, render the main application.
    return <DollySweepApp />;
}


// --- Core Application Logic Component ---
function DollySweepApp() {
    const [page, setPage] = useState('entry');
    const [db, setDb] = useState(null);
    const [auth, setAuth] = useState(null);
    const [isAuthReady, setIsAuthReady] = useState(false);

    // Initialize Firebase and handle Authentication
    useEffect(() => {
        try {
            const app = initializeApp(firebaseConfig);
            const firestoreDb = getFirestore(app);
            const firestoreAuth = getAuth(app);
            setDb(firestoreDb);
            setAuth(firestoreAuth);
            setLogLevel('debug');

            const unsubscribe = onAuthStateChanged(firestoreAuth, async (user) => {
                if (user) {
                    // User is signed in.
                    console.log("User is signed in:", user.uid);
                    setIsAuthReady(true);
                } else {
                    // No user is signed in, so sign in anonymously.
                    console.log("No user signed in, attempting to sign in anonymously...");
                    try {
                        await signInAnonymously(firestoreAuth);
                        console.log("Signed in anonymously successfully.");
                    } catch (error) {
                        console.error("Could not sign in anonymously:", error);
                    }
                }
            });
            return () => unsubscribe();
        } catch (error) {
            console.error("Firebase initialization error:", error);
        }
    }, []);


    const renderPage = () => {
        if (!isAuthReady) {
            return <LoadingSpinner message="Connecting to the database..." />;
        }
        switch (page) {
            case 'entry':
                return <DataEntryForm setPage={setPage} db={db} />;
            case 'view':
                return <DataViewPage db={db} />;
            case 'summary':
                return <SummaryPage db={db} />;
            default:
                return <DataEntryForm setPage={setPage} db={db} />;
        }
    };

    return (
        <div className="bg-gradient-to-br from-slate-50 to-slate-200 min-h-screen font-sans text-slate-800">
            <div className="container mx-auto p-4 max-w-7xl">
                <Header />
                <NavBar page={page} setPage={setPage} />
                <main className="bg-white/70 backdrop-blur-sm rounded-xl shadow-xl p-4 sm:p-6 md:p-8 mt-6 border border-slate-200">
                    {renderPage()}
                </main>
                <Footer />
            </div>
        </div>
    );
}

// --- UI Components ---

const LiveClock = () => {
    const [time, setTime] = useState('');

    useEffect(() => {
        const timer = setInterval(() => {
            const options = {
                timeZone: 'Asia/Kuala_Lumpur',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            };
            setTime(new Date().toLocaleString('en-US', options));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="flex items-center justify-center gap-2 text-sm text-slate-500 bg-slate-200/50 rounded-full px-4 py-1.5">
            <Clock className="w-4 h-4" />
            <span>{time} (MYT)</span>
        </div>
    );
};


const FirebaseConfigError = () => (
    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-8 rounded-lg shadow-lg max-w-4xl mx-auto my-10">
        <div className="flex">
            <div className="py-1"><AlertTriangle className="h-8 w-8 text-red-500 mr-4" /></div>
            <div>
                <p className="text-2xl font-bold">Firebase Configuration Missing</p>
                <p className="mt-2 text-lg">The application is not connected to its database. This is usually because the Firebase keys are missing.</p>
                <p className="mt-4 font-semibold">To fix this, please follow these steps:</p>
                <ol className="list-decimal list-inside mt-2 space-y-2">
                    <li>Open the <strong>`src/App.js`</strong> file in your code editor.</li>
                    <li>Find the `firebaseConfig` object near the top of the file.</li>
                    <li>Replace the placeholder values with the actual keys from your project's settings in the Firebase Console.</li>
                    <li>Save the file. The application should automatically reload with the correct configuration.</li>
                </ol>
            </div>
        </div>
    </div>
);

const Header = () => (
    <header className="text-center mb-6 pt-4">
        <center><img src="https://www.posaviation.com.my/media/hcrns023/loga1.png" alt="Pos Aviation" /></center>
        <h1 className="text-5xl sm:text-6xl font-bold text-slate-800 tracking-tight">PEN AH - Dolly Sweep Activity Tracker</h1>
        <p className="text-slate-500 mt-3 text-lg">Non-Motorized Equipment Tracking System</p>
        <div className="mt-4 flex justify-center">
            <LiveClock />
        </div>
    </header>
);

const NavBar = ({ page, setPage }) => {
    const navItems = [
        { id: 'entry', label: 'Data Entry', icon: PlusCircle },
        { id: 'view', label: 'View Data', icon: List },
        { id: 'summary', label: 'Summary', icon: BarChart2 },
    ];

    return (
        <nav className="bg-white/60 backdrop-blur-sm rounded-xl shadow-lg p-2 flex justify-center items-center space-x-2 sm:space-x-4 border border-slate-200/80">
            {navItems.map(item => (
                <button
                    key={item.id}
                    onClick={() => setPage(item.id)}
                    className={`flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg transition-all duration-300 ease-in-out text-sm sm:text-base font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-100
                        ${page === item.id ? 'bg-blue-600 text-white shadow-md' : 'text-slate-600 hover:bg-slate-200/70 hover:text-slate-800'}`}
                >
                    <item.icon className="w-5 h-5 mr-2.5" />
                    {item.label}
                </button>
            ))}
        </nav>
    );
};

const Footer = () => (
    <footer className="text-center mt-10 mb-4 text-sm text-slate-400">
        <p>&copy; {new Date().getFullYear()} Pos Aviation PEN Aircraft Handling - Dolly Sweep Activity Tracker. All rights reserved.</p>
    </footer>
);

const LoadingSpinner = ({ message }) => (
    <div className="flex flex-col items-center justify-center h-64 text-slate-500">
        <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p className="mt-4 text-lg">{message || "Loading..."}</p>
    </div>
);

const Modal = ({ show, title, message, onClose }) => {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 transition-opacity duration-300">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-11/12 max-w-md text-center transform transition-all duration-300 scale-95 opacity-0 animate-modal-in">
                <div className={`flex justify-center items-center mx-auto mb-4 w-16 h-16 rounded-full ${title.toLowerCase().includes('success') ? 'bg-green-100' : 'bg-red-100'}`}>
                    {title.toLowerCase().includes('success') ? <CheckCircle className="w-10 h-10 text-green-600" /> : <AlertTriangle className="w-10 h-10 text-red-600" />}
                </div>
                <h3 className="text-2xl font-bold mb-2 text-slate-800">{title}</h3>
                <p className="text-slate-600 mb-6">{message}</p>
                <button
                    onClick={onClose}
                    className="bg-blue-600 text-white font-bold py-2.5 px-8 rounded-lg hover:bg-blue-700 transition-all duration-300 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    Close
                </button>
            </div>
        </div>
    );
};


// --- Page Components ---

const DataEntryForm = ({ setPage, db }) => {
    const today = new Date().toISOString().split('T')[0];
    const [formData, setFormData] = useState({
        date: today,
        equipmentType: '10ftr Dolly',
        equipmentId: '',
        location: 'FEM',
        status: 'Serviceable',
        remarks: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [modalState, setModalState] = useState({ show: false, title: '', message: '' });

    const equipmentTypes = ['10ftr Dolly', '20ftr Dolly', '10ftr IP Dolly', 'Container Dolly', 'Baggage Trolley', 'Freight Trolley', 'HUM Trolley'];
    const locations = ['FEM', 'Terminal Pax', 'Dolly Yard', 'Terminal Cargo', 'FAA', 'Cargo Warehouse'];
    const statuses = ['Serviceable', 'Unserviceable'];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.equipmentId.trim()) {
            setModalState({ show: true, title: 'Validation Error', message: 'Equipment ID is required.' });
            return;
        }
        setIsLoading(true);

        try {
            const collectionRef = collection(db, `artifacts/${appId}/public/data/equipment_logs`);
            const q = query(collectionRef, where("date", "==", formData.date), where("equipmentId", "==", formData.equipmentId.trim()));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                setModalState({ show: true, title: 'Duplicate Entry', message: `Equipment ID '${formData.equipmentId}' has already been logged for ${formData.date}.` });
                setIsLoading(false);
                return;
            }

            await addDoc(collectionRef, {
                ...formData,
                equipmentId: formData.equipmentId.trim(),
                createdAt: Timestamp.now()
            });

            setModalState({ show: true, title: 'Success!', message: 'Equipment data has been saved successfully.' });
            
            setFormData({
                date: today,
                equipmentType: '10ftr Dolly',
                equipmentId: '',
                location: 'FEM',
                status: 'Serviceable',
                remarks: '',
            });

        } catch (error) {
            console.error("Error saving data: ", error);
            setModalState({ show: true, title: 'Error', message: 'Failed to save data. Please check the console for details.' });
        } finally {
            setIsLoading(false);
        }
    };
    
    const closeModal = () => setModalState({ show: false, title: '', message: '' });

    return (
        <>
            <Modal show={modalState.show} title={modalState.title} message={modalState.message} onClose={closeModal} />
            <form onSubmit={handleSubmit} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                    <div className="space-y-8">
                        <FormInput label="Date" type="date" name="date" value={formData.date} onChange={handleChange} />
                        <FormRadioGroup label="Type of Equipment" name="equipmentType" options={equipmentTypes} selected={formData.equipmentType} onChange={handleChange} />
                    </div>
                    <div className="space-y-8">
                        <FormInput label="Equipment ID" type="text" name="equipmentId" value={formData.equipmentId} onChange={handleChange} placeholder="e.g., ECD 4001" required />
                        <FormRadioGroup label="Location" name="location" options={locations} selected={formData.location} onChange={handleChange} />
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10">
                     <FormRadioGroup label="Status" name="status" options={statuses} selected={formData.status} onChange={handleChange} />
                     <FormTextArea label="Remarks" name="remarks" value={formData.remarks} onChange={handleChange} placeholder="Add any relevant notes..." />
                </div>
                <div className="pt-4 flex justify-end">
                    <button type="submit" disabled={isLoading} className="flex items-center justify-center bg-blue-600 text-white font-bold py-3 px-8 rounded-lg shadow-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105">
                        {isLoading ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : (
                            <>
                                <Server className="w-5 h-5 mr-2" />
                                Save Data
                            </>
                        )}
                    </button>
                </div>
            </form>
        </>
    );
};

const DataViewPage = ({ db }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [activeDates, setActiveDates] = useState(new Set());

    useEffect(() => {
        const fetchActiveDates = async () => {
            if (!db) return;
            const collectionRef = collection(db, `artifacts/${appId}/public/data/equipment_logs`);
            const querySnapshot = await getDocs(collectionRef);
            const dates = new Set(querySnapshot.docs.map(doc => doc.data().date));
            setActiveDates(dates);
        };
        fetchActiveDates();
    }, [db]);

    useEffect(() => {
        const fetchDataForDate = async () => {
            if (!db || !selectedDate) return;
            setIsLoading(true);
            setData([]);
            const dateString = selectedDate.toISOString().split('T')[0];
            const collectionRef = collection(db, `artifacts/${appId}/public/data/equipment_logs`);
            const q = query(collectionRef, where("date", "==", dateString));
            
            try {
                const querySnapshot = await getDocs(q);
                const fetchedData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                fetchedData.sort((a, b) => a.equipmentId.localeCompare(b.equipmentId));
                setData(fetchedData);
            } catch (error) {
                console.error("Error fetching data: ", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchDataForDate();
    }, [db, selectedDate]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
                <h2 className="text-2xl font-bold text-slate-700 mb-4 flex items-center"><Calendar className="mr-3 text-blue-600"/>Select Date</h2>
                <div className="bg-slate-50/70 p-4 rounded-lg border">
                    <CustomCalendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} activeDates={activeDates} />
                </div>
            </div>
            <div className="lg:col-span-2">
                <h2 className="text-2xl font-bold text-slate-700 mb-4 flex items-center"><FileText className="mr-3 text-blue-600"/>Logged Equipment for {selectedDate.toLocaleDateString('en-CA')}</h2>
                <div className="overflow-x-auto bg-white rounded-lg shadow-md border">
                    {isLoading ? <LoadingSpinner message="Fetching data..." /> :
                        data.length > 0 ? <DataTable data={data} /> :
                        <div className="text-center p-16 text-slate-500">
                            <List className="w-16 h-16 mx-auto text-slate-400" />
                            <p className="font-semibold mt-4 text-lg">No data found for this date.</p>
                            <p>Select another date from the calendar with a green dot.</p>
                        </div>
                    }
                </div>
            </div>
        </div>
    );
};

const SummaryPage = ({ db }) => {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [summaryData, setSummaryData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeDates, setActiveDates] = useState(new Set());

    useEffect(() => {
        const fetchActiveDates = async () => {
            if (!db) return;
            const collectionRef = collection(db, `artifacts/${appId}/public/data/equipment_logs`);
            const querySnapshot = await getDocs(collectionRef);
            const dates = new Set(querySnapshot.docs.map(doc => doc.data().date));
            setActiveDates(dates);
        };
        fetchActiveDates();
    }, [db]);

    useEffect(() => {
        const generateSummary = async () => {
            if (!db || !selectedDate) return;
            setIsLoading(true);
            setSummaryData(null);
            const dateString = selectedDate.toISOString().split('T')[0];
            const collectionRef = collection(db, `artifacts/${appId}/public/data/equipment_logs`);
            const q = query(collectionRef, where("date", "==", dateString));

            try {
                const querySnapshot = await getDocs(q);
                const data = querySnapshot.docs.map(doc => doc.data());

                if (data.length === 0) {
                    setSummaryData({ total: 0, statusCounts: {}, typeBreakdown: {}, locationSummary: {} });
                    return;
                }

                const total = data.length;
                const statusCounts = data.reduce((acc, item) => {
                    acc[item.status] = (acc[item.status] || 0) + 1;
                    return acc;
                }, {});

                const typeBreakdown = data.reduce((acc, item) => {
                    if (!acc[item.equipmentType]) {
                        acc[item.equipmentType] = { Serviceable: 0, Unserviceable: 0, total: 0 };
                    }
                    acc[item.equipmentType][item.status] = (acc[item.equipmentType][item.status] || 0) + 1;
                    acc[item.equipmentType].total++;
                    return acc;
                }, {});

                const locationSummary = data.reduce((acc, item) => {
                    if (!acc[item.location]) {
                        acc[item.location] = [];
                    }
                    acc[item.location].push(item.equipmentType);
                    return acc;
                }, {});

                setSummaryData({ total, statusCounts, typeBreakdown, locationSummary });

            } catch (error) {
                console.error("Error generating summary: ", error);
            } finally {
                setIsLoading(false);
            }
        };
        generateSummary();
    }, [db, selectedDate]);

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
                <h2 className="text-2xl font-bold text-slate-700 mb-4 flex items-center"><Calendar className="mr-3 text-blue-600"/>Select Date for Summary</h2>
                <div className="bg-slate-50/70 p-4 rounded-lg border">
                    <CustomCalendar selectedDate={selectedDate} setSelectedDate={setSelectedDate} activeDates={activeDates} />
                </div>
            </div>
            <div className="lg:col-span-2">
                 <h2 className="text-2xl font-bold text-slate-700 mb-4 flex items-center"><BarChart2 className="mr-3 text-blue-600"/>Summary for {selectedDate.toLocaleDateString('en-CA')}</h2>
                 {isLoading ? <LoadingSpinner message="Generating summary..."/> :
                    !summaryData || summaryData.total === 0 ? 
                    <div className="text-center p-16 text-slate-500 bg-white rounded-lg shadow-md border">
                        <BarChart2 className="w-16 h-16 mx-auto text-slate-400" />
                        <p className="font-semibold mt-4 text-lg">No data to summarize for this date.</p>
                        <p>Select a date with activity to view its summary.</p>
                    </div> :
                    <div className="space-y-6">
                        <SummaryCards total={summaryData.total} statusCounts={summaryData.statusCounts} />
                        <EquipmentBreakdown data={summaryData.typeBreakdown} />
                        <LocationSummary data={summaryData.locationSummary} />
                    </div>
                 }
            </div>
        </div>
    );
};


// --- Form & Data Display Components ---

const FormInput = ({ label, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-slate-600 mb-2">{label}</label>
        <input {...props} className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" />
    </div>
);

const FormTextArea = ({ label, ...props }) => (
    <div>
        <label className="block text-sm font-medium text-slate-600 mb-2">{label}</label>
        <textarea {...props} rows="4" className="w-full px-4 py-2.5 border border-slate-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"></textarea>
    </div>
);

const FormRadioGroup = ({ label, name, options, selected, onChange }) => (
    <div>
        <label className="block text-sm font-medium text-slate-600 mb-2">{label}</label>
        <div className="flex flex-wrap gap-2">
            {options.map(option => (
                <label key={option} className={`flex items-center px-3 py-1.5 border rounded-full cursor-pointer transition-all duration-200 text-sm font-medium ${selected === option ? 'bg-blue-600 border-blue-600 text-white shadow' : 'bg-white border-slate-300 hover:bg-slate-100 hover:border-slate-400'}`}>
                    <input type="radio" name={name} value={option} checked={selected === option} onChange={onChange} className="sr-only" />
                    <span>{option}</span>
                </label>
            ))}
        </div>
    </div>
);

const DataTable = ({ data }) => (
    <table className="w-full text-sm text-left text-slate-500">
        <thead className="text-xs text-slate-700 uppercase bg-slate-100/80">
            <tr>
                <th scope="col" className="px-6 py-4">Equipment ID</th>
                <th scope="col" className="px-6 py-4">Type</th>
                <th scope="col" className="px-6 py-4">Location</th>
                <th scope="col" className="px-6 py-4">Status</th>
                <th scope="col" className="px-6 py-4">Remarks</th>
            </tr>
        </thead>
        <tbody>
            {data.map(item => (
                <tr key={item.id} className="bg-white border-b last:border-b-0 hover:bg-slate-50/70 transition-colors">
                    <th scope="row" className="px-6 py-4 font-medium text-slate-900 whitespace-nowrap">{item.equipmentId}</th>
                    <td className="px-6 py-4">{item.equipmentType}</td>
                    <td className="px-6 py-4">{item.location}</td>
                    <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${item.status === 'Serviceable' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {item.status}
                        </span>
                    </td>
                    <td className="px-6 py-4 max-w-xs truncate" title={item.remarks}>{item.remarks || <span className="text-slate-400">N/A</span>}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const CustomCalendar = ({ selectedDate, setSelectedDate, activeDates }) => {
    const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1));

    const changeMonth = (amount) => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + amount, 1));
    };

    const daysInMonth = useMemo(() => new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate(), [currentMonth]);
    const firstDayOfMonth = useMemo(() => new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay(), [currentMonth]);

    const renderDays = () => {
        const days = Array.from({ length: firstDayOfMonth }, (_, i) => <div key={`blank-${i}`} className="p-1"></div>);
        
        for (let day = 1; day <= daysInMonth; day++) {
            const fullDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
            const dateString = fullDate.toISOString().split('T')[0];
            const isSelected = selectedDate && selectedDate.toDateString() === fullDate.toDateString();
            const isActive = activeDates.has(dateString);
            const isToday = new Date().toDateString() === fullDate.toDateString();

            let dayClasses = "relative w-10 h-10 flex items-center justify-center rounded-full cursor-pointer transition-colors duration-200 text-sm ";
            if (isSelected) {
                dayClasses += "bg-blue-600 text-white font-bold shadow";
            } else if (isToday) {
                dayClasses += "bg-blue-100 text-blue-700 font-bold";
            } else {
                dayClasses += "hover:bg-slate-200";
            }
            
            days.push(
                <div key={day} className="p-1 flex justify-center items-center">
                    <button onClick={() => setSelectedDate(fullDate)} className={dayClasses}>
                        {day}
                        {isActive && <span className="absolute bottom-1 w-1.5 h-1.5 bg-green-500 rounded-full"></span>}
                    </button>
                </div>
            );
        }
        return days;
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center mb-4 px-1">
                <button onClick={() => changeMonth(-1)} className="p-2 rounded-full hover:bg-slate-200 transition-colors"><ChevronLeft/></button>
                <h3 className="font-bold text-lg text-slate-700">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
                <button onClick={() => changeMonth(1)} className="p-2 rounded-full hover:bg-slate-200 transition-colors"><ChevronRight/></button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-xs text-slate-500 font-medium mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(d => <div key={d}>{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {renderDays()}
            </div>
        </div>
    );
};

const SummaryCards = ({ total, statusCounts }) => {
    const serviceable = statusCounts?.Serviceable || 0;
    const unserviceable = statusCounts?.Unserviceable || 0;
    
    const cards = [
        { label: 'Total Checked', value: total, icon: HardHat, color: 'blue' },
        { label: 'Serviceable', value: serviceable, icon: CheckCircle, color: 'green' },
        { label: 'Unserviceable', value: unserviceable, icon: XCircle, color: 'red' },
    ];

    const colors = {
        blue: 'from-blue-100 to-blue-200 text-blue-900',
        green: 'from-green-100 to-green-200 text-green-900',
        red: 'from-red-100 to-red-200 text-red-900',
    };

    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {cards.map(card => (
                 <div key={card.label} className={`bg-gradient-to-br ${colors[card.color]} p-5 rounded-xl shadow-lg border border-white/50`}>
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold">{card.label}</h4>
                        <card.icon className="w-6 h-6 opacity-70" />
                    </div>
                    <p className="text-4xl font-bold mt-2">{card.value}</p>
                </div>
            ))}
        </div>
    );
};

const EquipmentBreakdown = ({ data }) => (
    <div className="bg-white/80 p-6 rounded-xl shadow-lg border border-slate-200/80">
        <h3 className="text-xl font-bold text-slate-700 mb-5">Equipment Breakdown</h3>
        <div className="space-y-5">
            {Object.entries(data).sort((a,b) => b[1].total - a[1].total).map(([type, counts]) => (
                <div key={type}>
                    <div className="flex justify-between items-center mb-1.5">
                        <span className="font-semibold text-slate-700">{type}</span>
                        <span className="text-sm font-bold text-slate-500">{counts.total} total</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-2.5">
                        <div className="bg-green-500 h-2.5 rounded-l-full" style={{ width: `${(counts.Serviceable / counts.total) * 100}%` }}></div>
                    </div>
                    <div className="flex justify-between text-xs mt-1.5 text-slate-500">
                        <span><span className="font-bold text-green-600">{counts.Serviceable || 0}</span> Serviceable</span>
                        <span><span className="font-bold text-red-600">{counts.Unserviceable || 0}</span> Unserviceable</span>
                    </div>
                </div>
            ))}
        </div>
    </div>
);

const LocationSummary = ({ data }) => (
    <div className="bg-white/80 p-6 rounded-xl shadow-lg border border-slate-200/80">
        <h3 className="text-xl font-bold text-slate-700 mb-5">Summary by Location</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            {Object.entries(data).sort((a,b) => b[1].length - a[1].length).map(([location, items]) => (
                <div key={location}>
                    <h4 className="font-bold text-slate-700 flex items-center mb-2"><MapPin className="w-4 h-4 mr-2 text-blue-500"/>{location} ({items.length})</h4>
                    <ul className="space-y-1.5 text-sm text-slate-600">
                        {items.slice(0, 5).map((item, index) => <li key={index} className="flex items-center"><span className="w-1.5 h-1.5 bg-slate-400 rounded-full mr-2"></span>{item}</li>)}
                        {items.length > 5 && <li className="font-semibold text-slate-500 mt-1">...and {items.length - 5} more</li>}
                    </ul>
                </div>
            ))}
        </div>
    </div>
);